import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Avatar,
  message,
  Row,
  Col,
  Typography,
  Divider,
  Tag,
} from 'antd';
import { UserOutlined, CrownOutlined } from '@ant-design/icons';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { authApi } from '@tradermind/shared/api';
import { useAuthStore } from '@tradermind/shared/stores';
import { updateProfileSchema, type UpdateProfileFormData } from '@tradermind/shared/validators';
import { formatDate } from '@tradermind/shared/utils';

const { Title, Text } = Typography;

export default function ProfilePage() {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const { user, updateUser } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateProfileFormData>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      nickname: user?.nickname || '',
      avatar_url: user?.avatar_url || '',
    },
  });

  const updateMutation = useMutation({
    mutationFn: authApi.updateProfile,
    onSuccess: (response) => {
      updateUser(response.data);
      message.success(t('messages.update_success'));
      setIsEditing(false);
    },
    onError: () => {
      message.error(t('messages.update_failed'));
    },
  });

  const onSubmit = (data: UpdateProfileFormData) => {
    updateMutation.mutate(data);
  };

  const handleCancel = () => {
    reset({
      nickname: user?.nickname || '',
      avatar_url: user?.avatar_url || '',
    });
    setIsEditing(false);
  };

  return (
    <div>
      <Title level={4}>{t('title')}</Title>

      <Row gutter={24}>
        <Col xs={24} lg={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Avatar size={100} src={user?.avatar_url} icon={<UserOutlined />} />
              <div style={{ marginTop: 16 }}>
                <Title level={4} style={{ margin: 0 }}>
                  {user?.nickname || t('user')}
                </Title>
                <Text type="secondary">{user?.email}</Text>
              </div>
              <div style={{ marginTop: 16 }}>
                {user?.premium ? (
                  <Tag icon={<CrownOutlined />} color="gold">
                    {t('premium_member')}
                  </Tag>
                ) : (
                  <Tag>{t('free_user')}</Tag>
                )}
              </div>
              {user?.premium && user.premium_expire_at && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">
                    {t('expire_time')}: {formatDate(user.premium_expire_at)}
                  </Text>
                </div>
              )}
            </div>

            <Divider />

            <div>
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">{t('register_time')}</Text>
                <div>{user?.created_at ? formatDate(user.created_at) : '-'}</div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card
            title={t('basic_info')}
            extra={
              !isEditing && (
                <Button type="link" onClick={() => setIsEditing(true)}>
                  {t('edit')}
                </Button>
              )
            }
          >
            {isEditing ? (
              <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
                <Form.Item
                  label={t('nickname')}
                  validateStatus={errors.nickname ? 'error' : ''}
                  help={errors.nickname?.message}
                >
                  <Controller
                    name="nickname"
                    control={control}
                    render={({ field }) => <Input {...field} placeholder={t('nickname_placeholder')} />}
                  />
                </Form.Item>

                <Form.Item
                  label={t('avatar_url')}
                  validateStatus={errors.avatar_url ? 'error' : ''}
                  help={errors.avatar_url?.message}
                >
                  <Controller
                    name="avatar_url"
                    control={control}
                    render={({ field }) => <Input {...field} placeholder={t('avatar_url_placeholder')} />}
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={updateMutation.isPending}
                    style={{ marginRight: 8 }}
                  >
                    {t('common:save')}
                  </Button>
                  <Button onClick={handleCancel}>{t('common:cancel')}</Button>
                </Form.Item>
              </Form>
            ) : (
              <div>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text type="secondary">{t('nickname')}</Text>
                    <div style={{ fontSize: 16 }}>{user?.nickname || '-'}</div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary">{t('email')}</Text>
                    <div style={{ fontSize: 16 }}>{user?.email}</div>
                  </Col>
                </Row>
              </div>
            )}
          </Card>

          <Card title={t('account_security')} style={{ marginTop: 24 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <Text strong>{t('login_password')}</Text>
                <div>
                  <Text type="secondary">{t('password_hint')}</Text>
                </div>
              </div>
              <Button onClick={() => navigate('/profile/change-password')}>{t('change_password')}</Button>
            </div>
          </Card>

          {!user?.premium && (
            <Card
              title={t('upgrade_page.title')}
              style={{ marginTop: 24, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              <div style={{ color: '#fff' }}>
                <Title level={4} style={{ color: '#fff', margin: 0 }}>
                  {t('upgrade_page.unlock_all')}
                </Title>
                <ul style={{ marginTop: 16, paddingLeft: 20 }}>
                  <li>{t('upgrade_page.feature1')}</li>
                  <li>{t('upgrade_page.feature2')}</li>
                  <li>{t('upgrade_page.feature3')}</li>
                  <li>{t('upgrade_page.feature4')}</li>
                </ul>
                <Button
                  type="primary"
                  size="large"
                  style={{ marginTop: 16, background: '#fff', color: '#764ba2' }}
                >
                  {t('upgrade_page.upgrade_now')}
                </Button>
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
